import { decrypt, encrypt } from "../storage/cipher";
import { isValidPlaylistUrl } from "../../utils/util";
import { PLAYLIST_SCHEMA_VERSION } from "./constants";
import { classifyError, failure, storageError } from "./errors";
import { computeFingerprint, randomUid } from "./identity";
import { LocalStorageAdapter } from "./LocalStorageAdapter";
import { fingerprintTag, logPlaylist, setPlaylistLogPlatform } from "./logger";
import { PlaylistMigrationService } from "./PlaylistMigrationService";
import { getLocalStorage } from "./PlatformCapabilities";
import { defaultMeta, toPlaylist } from "./records";

const NAME_MAX = 100;
const URL_MAX = 4096;
// Tombstones are tiny and pruned by count only: pruning by age would trust the TV clock, and a clock far in
// the future would wipe them all at once.
const TOMBSTONE_MAX = 500;

// An unpaired surrogate can't be turned into UTF-8; hashing or encrypting it throws.
const HAS_LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?:[^\ud800-\udbff]|^)[\udc00-\udfff]/;

// name and url required, trimmed, length-limited, no control characters, and the url must look like a playlist url
const validateInput = (input) => {

    if (!input || typeof input !== "object") return null;
    if (typeof input.name !== "string" || typeof input.url !== "string") return null;

    const name = input.name.trim();
    const url = input.url.trim();

    if (!name || name.length > NAME_MAX || /[\u0000-\u001f]/.test(name) || HAS_LONE_SURROGATE.test(name)) return null;
    if (!url || url.length > URL_MAX || !isValidPlaylistUrl(url) || HAS_LONE_SURROGATE.test(url)) return null;

    let epg;

    if (input.epg_url !== undefined && input.epg_url !== null && input.epg_url !== "") {
        if (typeof input.epg_url !== "string") return null;
        epg = input.epg_url.trim();
        if (epg.length > URL_MAX || !isValidPlaylistUrl(epg) || HAS_LONE_SURROGATE.test(epg)) return null;
    }

    return { name, url, epg };

};

// the fingerprint, or null if the text can't be hashed; nothing in the add path may throw
const fingerprintOf = (url) => {
    try {
        return computeFingerprint(url);
    } catch (e) {
        return null;
    }
};

// Runs one operation at a time, in arrival order. A failing operation never blocks the next.
class SerialQueue {

    constructor() {
        this.tail = Promise.resolve();
    }

    run(fn) {
        const result = this.tail.then(fn, fn);
        this.tail = result.then(() => undefined, () => undefined);
        return result;
    }

}

/**
 * The single point of access to the playlists on this device. Nothing else reads or writes them.
 *
 * - They live in localStorage (`xp.playlists.v1`, see LocalStorageAdapter). The legacy `playlists` key is
 *   only ever a read-only migration source.
 * - Every operation runs through one queue, so "check for a duplicate, then write" can't interleave with
 *   another request.
 * - A success is returned only after the write has been read back, and the in-memory list is replaced only
 *   after that: a failed save changes nothing.
 * - Results are { success: true, ... } or { success: false, code, message }; `message` is ready to show.
 *
 * The app sees playlists as { id, name, url, epg_url?, is_selected, createdAt?, updatedAt?, status?, ... }.
 */
export class PlaylistStorageService {

    constructor(options = {}) {

        this.adapter = options.adapter !== undefined ? options.adapter : new LocalStorageAdapter();
        this.now = options.now || (() => Date.now());
        this.newUid = options.uid || randomUid;

        this.migration = new PlaylistMigrationService({
            now: this.now,
            legacyStorage: options.legacyStorage !== undefined ? options.legacyStorage : getLocalStorage(),
        });

        this.queue = new SerialQueue();
        this.listeners = [];

        // idle | initializing | ready | unavailable
        this.state = "idle";
        // { playlists, tombstones, meta } as last committed; null until init() has resolved
        this.core = null;
        // uid -> { url, epg, urlEnc, epgEnc }, the decrypted cache
        this.plain = {};
        this.corrupt = 0;
        this.errorCode = undefined;
        this.errorReason = undefined;

    }

    // ---- lifecycle

    /** Idempotent and never rejects. Resolves once the store is loaded and migrated, or found unusable. */
    init() {
        return this.run(async () => this.getStatus());
    }

    /** For a store that failed to start: tries again, changing nothing on disk first. */
    retryInit() {
        return this.queue.run(async () => {
            if (this.state === "unavailable") this.state = "idle";
            await this.ensureInit();
            return this.getStatus();
        });
    }

    setPlatform(label) {
        setPlaylistLogPlatform(label);
    }

    getStatus() {

        const meta = this.core && this.core.meta;

        return {
            state: this.state,
            playlists: this.core ? this.core.playlists.length : 0,
            corruptRecords: this.corrupt,
            legacyImported: !!meta && meta.legacyImportedAt !== undefined,
            serverImported: !!meta && meta.PlaylistGeted === true,
            errorCode: this.errorCode,
            errorReason: this.errorReason,
        };

    }

    /**
     * Per-playlist data (favourites, watch history) is pruned to the list it is given, so it must not be
     * pruned while that list may be incomplete: the store failed to start, or records couldn't be read.
     */
    canPruneOrphanData() {
        return this.state === "ready" && this.corrupt === 0;
    }

    // ---- reads

    /** Synchronous view of the last committed state. Empty until init() has resolved. */
    getAll() {
        if (!this.core) return [];
        return this.core.playlists.map((record) => this.publicView(record));
    }

    /** Early, cheap answer for a form that would otherwise log in to a provider first. `add` stays authoritative. */
    findDuplicate(url) {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            if (typeof url !== "string" || !url.trim()) return { success: true, exists: false };

            const fingerprint = fingerprintOf(url.trim());
            if (!fingerprint) return { success: true, exists: false };

            return { success: true, exists: this.core.playlists.some((p) => p.fingerprint === fingerprint) };

        });
    }

    subscribe(listener) {

        this.listeners.push(listener);

        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };

    }

    // ---- writes

    /** { name, url, epg_url? } -> { success, playlist } | PLAYLIST_ALREADY_EXISTS | INVALID_PLAYLIST | ... */
    add(input) {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            const valid = validateInput(input);
            if (!valid) return failure("INVALID_PLAYLIST");

            const fingerprint = fingerprintOf(valid.url);
            if (!fingerprint) return failure("INVALID_PLAYLIST");

            if (this.core.playlists.some((p) => p.fingerprint === fingerprint)) {
                logPlaylist("warn", "duplicate_rejected", { op: "add", fp: fingerprintTag(fingerprint) });
                return failure("PLAYLIST_ALREADY_EXISTS");
            }

            const urlEnc = encrypt(valid.url);
            const epgUrlEnc = valid.epg ? encrypt(valid.epg) : "";
            if (!urlEnc || (valid.epg && !epgUrlEnc)) return failure("STORAGE_ERROR");

            // never older than a deletion of the same playlist, whatever the clock says, so that deletion
            // can't swallow this add in a later merge
            let now = this.now();
            this.core.tombstones.forEach((t) => { if (t.fingerprint === fingerprint && t.deletedAt >= now) now = t.deletedAt + 1; });

            const record = {
                uid: this.newUid(),
                id: this.nextId(now),
                name: valid.name,
                urlEnc,
                fingerprint,
                schemaVersion: PLAYLIST_SCHEMA_VERSION,
                createdAt: now,
                updatedAt: now,
                origin: "tv",
            };

            if (epgUrlEnc) record.epgUrlEnc = epgUrlEnc;

            try {
                await this.adapter.commit({ put: [record] });
            } catch (e) {
                return this.failed("add", e);
            }

            this.core = { ...this.core, playlists: this.core.playlists.concat(record) };
            this.plain[record.uid] = { url: valid.url, epg: valid.epg, urlEnc, epgEnc: epgUrlEnc || undefined };
            this.emit();

            return { success: true, playlist: this.publicView(record) };

        });
    }

    /** Leaves a tombstone (no URL) so an old source can never bring the playlist back. */
    remove(id) {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            const record = this.findById(id);
            if (!record) return failure("NOT_FOUND");

            // never older than the record it deletes, whatever the clock says
            const tombstone = {
                uid: record.uid,
                id: record.id,
                fingerprint: record.fingerprint,
                deletedAt: Math.max(this.now(), record.updatedAt),
            };

            const view = this.publicView(record);
            const tombstones = this.core.tombstones.filter((t) => t.uid !== tombstone.uid).concat(tombstone);
            const stale = this.staleTombstones(tombstones);

            try {
                await this.adapter.commit({ remove: [record.uid], putTombstones: [tombstone], removeTombstones: stale });
            } catch (e) {
                return this.failed("remove", e);
            }

            this.core = {
                ...this.core,
                playlists: this.core.playlists.filter((p) => p.uid !== record.uid),
                tombstones: tombstones.filter((t) => stale.indexOf(t.uid) === -1),
            };

            delete this.plain[record.uid];
            this.emit();

            return { success: true, playlist: view };

        });
    }

    /** Marks one playlist selected and every other one not, in one write. Not an edit: `updatedAt` is untouched. */
    select(id) {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            const target = this.findById(id);
            if (!target) return failure("NOT_FOUND");

            const result = await this.commitSelection((p) => p.uid === target.uid);
            if (result.success === false) return result;

            return { success: true, playlist: this.publicView(result.playlists.filter((p) => p.uid === target.uid)[0]) };

        });
    }

    /** Unselects every playlist (select() can only move the selection), in one write. */
    clearSelection() {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            const result = await this.commitSelection(() => false);
            if (result.success === false) return result;

            return { success: true };

        });
    }

    /**
     * One-time import of the playlists in the register_device response. Sets `PlaylistGeted` once they are
     * saved and verified; while it is true this does nothing, whatever registration returns. A response without
     * a list (or a failed request) says nothing about the account and doesn't use the import up.
     */
    importServerPlaylists(list) {
        return this.run(async () => {

            const blocked = this.guard();
            if (blocked) return blocked;

            if (this.core.meta.PlaylistGeted) return { success: true, imported: 0, skipped: true };
            if (!Array.isArray(list)) return { success: true, imported: 0, skipped: true };

            try {

                const { state, report } = await this.migration.importServerList(this.channel(), this.core, list);

                this.adopt(state);
                this.emit();

                if (report.imported > 0) logPlaylist("warn", "server_import", { count: report.imported });

                return { success: true, imported: report.imported, skipped: false };

            } catch (e) {
                return this.failed("server_import", e);
            }

        });
    }

    // ---- internals

    run(fn) {
        return this.queue.run(async () => {
            await this.ensureInit();
            return fn();
        });
    }

    async ensureInit() {

        if (this.state !== "idle") return;

        try {
            await this.runInit();
        } catch (e) {
            // runInit handles every failure it expects; this is for the one it doesn't. Starting must never
            // reject, or the app would wait on it forever.
            this.core = null;
            this.state = "unavailable";
            this.errorCode = "STORAGE_ERROR";
            this.errorReason = "init_threw";
            logPlaylist("error", "init_threw", { op: "init", code: this.errorCode });
        }

    }

    async runInit() {

        this.state = "initializing";
        this.errorCode = undefined;
        this.errorReason = undefined;
        this.corrupt = 0;
        this.plain = {};
        this.core = null;

        try {

            if (!this.adapter) throw storageError("STORAGE_UNAVAILABLE", "no_adapter");

            await this.adapter.probe();

            const snapshot = await this.adapter.readAll();

            this.corrupt = snapshot.corrupt;

            const meta = snapshot.meta || defaultMeta(this.now());

            this.core = { playlists: this.hydrate(snapshot.playlists), tombstones: snapshot.tombstones, meta };

            if (meta.legacyImportedAt === undefined) {

                const { state, report } = await this.migration.migrateLegacy(this.channel(), this.core);

                this.adopt(state);

                if (report.imported > 0) logPlaylist("warn", "legacy_import", { op: "init", count: report.imported });

            }

            this.state = "ready";

        } catch (e) {

            // nothing usable: the app is told with a code, and nothing on disk has been touched
            const error = this.toError(e);

            this.core = null;
            this.plain = {};
            this.state = "unavailable";
            this.errorCode = error.code;
            this.errorReason = error.reason;

            logPlaylist("error", "storage_unavailable", { op: "init", code: this.errorCode, reason: this.errorReason });

        }

    }

    guard() {
        if (this.state !== "ready" || !this.core) return failure(this.errorCode || "STORAGE_UNAVAILABLE");
        return null;
    }

    findById(id) {
        return this.core.playlists.filter((p) => p.id === id)[0];
    }

    publicView(record) {
        return toPlaylist(record, this.plain[record.uid] || { url: "" });
    }

    // writes the selection `isSelected` describes -> { success, playlists } | failure
    async commitSelection(isSelected) {

        const changed = [];

        const playlists = this.core.playlists.map((p) => {
            const selected = isSelected(p);
            if (!!p.is_selected === selected) return p;
            const next = { ...p, is_selected: selected };
            changed.push(next);
            return next;
        });

        if (!changed.length) return { success: true, playlists };

        try {
            await this.adapter.commit({ put: changed });
        } catch (e) {
            return this.failed("select", e);
        }

        this.core = { ...this.core, playlists };
        this.emit();

        return { success: true, playlists };

    }

    emit() {

        const snapshot = this.getAll();

        this.listeners.slice().forEach((listener) => {
            try { listener(snapshot); } catch (e) { }
        });

    }

    /**
     * Ids are numbers because the whole app keys on them (`playlistData-<id>`). They are time-based so they
     * stay clear of the small ids the backend handed out, and never reuse an id that was ever deleted
     * (`playlistData-<id>` may still exist for it).
     */
    nextId(now) {

        let highest = 0;

        this.core.playlists.forEach((p) => { if (p.id > highest) highest = p.id; });
        this.core.tombstones.forEach((t) => { if (t.id > highest) highest = t.id; });

        return Math.max(Math.floor(now / 1000), highest + 1);

    }

    staleTombstones(tombstones) {
        return tombstones
            .slice()
            .sort((a, b) => b.deletedAt - a.deletedAt)
            .slice(TOMBSTONE_MAX)
            .map((t) => t.uid);
    }

    failed(op, e) {
        const error = this.toError(e);
        logPlaylist("error", op + "_failed", { adapter: this.adapter ? this.adapter.kind : undefined, code: error.code, reason: error.reason });
        return failure(error.code);
    }

    toError(e) {
        return classifyError(e, { code: "STORAGE_ERROR", reason: "unexpected" });
    }

    channel() {
        return {
            commit: (change) => this.adapter.commit(change),
            readBack: () => this.adapter.readAll(),
        };
    }

    // takes over the state a migration produced, decrypting what is new
    adopt(state) {
        this.core = { ...state, playlists: this.hydrate(state.playlists) };
    }

    // Fills the decrypted cache. A record that can't be decrypted is unusable here; it stays on disk, untouched.
    hydrate(records) {

        const usable = [];
        const nextPlain = {};

        records.forEach((record) => {

            // a cached value is reused only for the very ciphertext it came from
            const known = this.plain[record.uid];
            const fresh = known && known.urlEnc === record.urlEnc && known.epgEnc === record.epgUrlEnc ? known : null;

            const url = fresh ? fresh.url : decrypt(record.urlEnc);

            if (!url) {
                this.corrupt++;
                return;
            }

            const epg = fresh ? fresh.epg : record.epgUrlEnc ? decrypt(record.epgUrlEnc) || undefined : undefined;

            nextPlain[record.uid] = { url, epg, urlEnc: record.urlEnc, epgEnc: record.epgUrlEnc };
            usable.push(record);

        });

        this.plain = nextPlain;

        return usable;

    }

}
